import { Link } from 'react-router-dom'

import { getManifest } from '@senses/games'

import { Alert, Empty, Spinner } from '../components/ui.tsx'
import { api, type Assignment, type GameSummary } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useAsync } from '../lib/hooks.ts'
import { formatChange, formatNumber, formatRelative, plural } from '../lib/format.ts'

export function ClientDashboard(): React.JSX.Element {
  const { user } = useAuth()

  const assignments = useAsync(() => api.assignments.list(undefined, true), [])
  const summary = useAsync(() => api.sessions.summary(), [])

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>Dobrý den, {user?.name.split(' ')[0]}</h1>
          <p>Tohle máš od trenéra naplánované k tréninku.</p>
        </div>
      </div>

      {assignments.error && <Alert>{assignments.error}</Alert>}

      {assignments.loading ? (
        <Spinner />
      ) : assignments.data && assignments.data.assignments.length > 0 ? (
        <div className="grid">
          {assignments.data.assignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} />
          ))}
        </div>
      ) : (
        <Empty title="Zatím nemáš přiřazený žádný trénink">
          <p>Jakmile ti trenér nastaví program, objeví se tady.</p>
        </Empty>
      )}

      {summary.data && summary.data.summary.length > 0 && (
        <section className="section">
          <div className="section__title">
            <h2>Tvůj posun</h2>
          </div>
          <div className="grid">
            {summary.data.summary.map((item) => (
              <SummaryCard key={item.gameId} summary={item} />
            ))}
          </div>
        </section>
      )}
    </main>
  )
}

function AssignmentCard({ assignment }: { assignment: Assignment }): React.JSX.Element {
  const program = assignment.program
  const manifest = program ? getManifest(program.gameId) : undefined

  const done = assignment.completedSessions
  const target = assignment.targetSessions
  const ratio = target ? Math.min(1, done / target) : null

  return (
    <Link to={`/hra/${assignment.id}`} className="card card--link">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>{manifest?.icon ?? '🎯'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{program?.name ?? 'Neznámý program'}</h3>
          <p className="dim" style={{ fontSize: '0.85rem' }}>
            {manifest?.name ?? program?.gameId}
          </p>
        </div>
      </div>

      {program?.description && (
        <p className="muted" style={{ fontSize: '0.9rem', marginTop: '0.75rem' }}>
          {program.description}
        </p>
      )}

      {assignment.note && (
        <p style={{ fontSize: '0.88rem', marginTop: '0.75rem', color: 'var(--warning)' }}>
          Poznámka trenéra: {assignment.note}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginTop: '1rem',
          flexWrap: 'wrap',
        }}
      >
        {target ? (
          <span className={`badge ${ratio === 1 ? 'badge--success' : 'badge--accent'}`}>
            {done} / {target} {plural(target, 'série', 'série', 'sérií')}
          </span>
        ) : (
          <span className="badge">
            {done} {plural(done, 'série', 'série', 'sérií')}
          </span>
        )}

        {assignment.lastScore !== null && manifest && (
          <span className="badge">
            {manifest.primaryMetric.label} {formatNumber(assignment.lastScore, manifest.primaryMetric.precision)}
          </span>
        )}

        <span className="dim" style={{ fontSize: '0.82rem', marginLeft: 'auto' }}>
          {formatRelative(assignment.lastPlayedAt)}
        </span>
      </div>

      {!manifest && (
        <p className="field__error" style={{ marginTop: '0.75rem' }}>
          Tuto hru aplikace nezná — ozvi se trenérovi.
        </p>
      )}
    </Link>
  )
}

function SummaryCard({ summary }: { summary: GameSummary }): React.JSX.Element {
  const manifest = getManifest(summary.gameId)
  const metric = manifest?.primaryMetric
  const change = formatChange(summary.firstScore, summary.lastScore)
  const improved = summary.lastScore >= summary.firstScore

  return (
    <Link to={`/pokrok/${summary.gameId}`} className="card card--link">
      <h3>{manifest?.name ?? summary.gameId}</h3>

      <div className="stats" style={{ marginTop: '1rem' }}>
        <div>
          <div className="stat__label">{metric?.label ?? 'Výsledek'}</div>
          <div className="stat__value">{formatNumber(summary.lastScore, metric?.precision ?? 2)}</div>
        </div>
        <div>
          <div className="stat__label">Nejlepší</div>
          <div className="stat__value">{formatNumber(summary.bestScore, metric?.precision ?? 2)}</div>
        </div>
      </div>

      <p className="stat__hint" style={{ marginTop: '0.85rem' }}>
        {summary.sessions} {plural(summary.sessions, 'série', 'série', 'sérií')}
        {change && (
          <>
            {' · '}
            <span className={improved ? 'trend--up' : 'trend--down'}>{change} od začátku</span>
          </>
        )}
      </p>
    </Link>
  )
}
